import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import HeaderBar from '@/components/HeaderBar';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BillingSuccessPage({ token, user, logout }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('loading'); // loading, success, failed, expired
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [pollAttempts, setPollAttempts] = useState(0);

    const sessionId = searchParams.get('session_id');
    const maxPollAttempts = 10;
    const pollInterval = 2000;

    useEffect(() => {
        if (!sessionId) {
            setStatus('failed');
            return;
        }

        const pollPaymentStatus = async () => {
            if (pollAttempts >= maxPollAttempts) {
                setStatus('failed');
                return;
            }

            try {
                const response = await axios.get(
                    `${API}/api/checkout/status/${sessionId}`,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );

                const data = response.data;
                setPaymentDetails(data);

                if (data.payment_status === 'paid') {
                    setStatus('success');
                    return;
                } else if (data.status === 'expired') {
                    setStatus('expired');
                    return;
                }

                // Continue polling
                setPollAttempts(prev => prev + 1);
                setTimeout(pollPaymentStatus, pollInterval);

            } catch (error) {
                console.error('Error checking payment status:', error);
                setPollAttempts(prev => prev + 1);
                
                if (pollAttempts < maxPollAttempts - 1) {
                    setTimeout(pollPaymentStatus, pollInterval);
                } else {
                    setStatus('failed');
                }
            }
        };

        pollPaymentStatus();
    }, [sessionId, token]);

    if (status === 'loading') {
        return (
            <div className="settings-page flex flex-col"><HeaderBar user={user} logout={logout} /><div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        Processing your payment...
                    </h2>
                    <p className="text-gray-600">
                        Please wait while we confirm your subscription.
                    </p>
                </div>
            </div>
            </div>
        );
    }

    if (status === 'success') {
        return (
            <div className="settings-page flex flex-col"><HeaderBar user={user} logout={logout} /><div className="flex-1 flex items-center justify-center">
                <div className="max-w-md w-full mx-4">
                    <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle className="w-10 h-10 text-green-600" />
                        </div>
                        
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">
                            Payment Successful!
                        </h1>
                        
                        <p className="text-gray-600 mb-6">
                            Thank you for subscribing. Your plan has been activated and you now have access to all features.
                        </p>

                        {paymentDetails && (
                            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-gray-500">Amount paid</span>
                                    <span className="font-medium">
                                        ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500">Status</span>
                                    <span className="text-green-600 font-medium">Paid</span>
                                </div>
                            </div>
                        )}

                        <div className="space-y-3">
                            <Button
                                onClick={() => navigate('/dashboard')}
                                className="w-full bg-indigo-600 hover:bg-indigo-700"
                            >
                                Go to Dashboard
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                            
                            <Link
                                to="/settings/subscription"
                                className="block text-sm text-indigo-600 hover:text-indigo-700"
                            >
                                View subscription details
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        );
    }

    if (status === 'expired') {
        return (
            <div className="settings-page flex flex-col"><HeaderBar user={user} logout={logout} /><div className="flex-1 flex items-center justify-center">
                <div className="max-w-md w-full mx-4">
                    <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <XCircle className="w-10 h-10 text-yellow-600" />
                        </div>
                        
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">
                            Session Expired
                        </h1>
                        
                        <p className="text-gray-600 mb-6">
                            Your checkout session has expired. Please try again.
                        </p>

                        <Button
                            onClick={() => navigate('/billing/choose-plan')}
                            className="w-full bg-indigo-600 hover:bg-indigo-700"
                        >
                            Try Again
                        </Button>
                    </div>
                </div>
            </div>
            </div>
        );
    }

    // Failed state
    return (
        <div className="settings-page flex flex-col"><HeaderBar user={user} logout={logout} /><div className="flex-1 flex items-center justify-center">
            <div className="max-w-md w-full mx-4">
                <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <XCircle className="w-10 h-10 text-red-600" />
                    </div>
                    
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        Payment Failed
                    </h1>
                    
                    <p className="text-gray-600 mb-6">
                        We couldn't process your payment. Please check your payment details and try again.
                    </p>

                    <div className="space-y-3">
                        <Button
                            onClick={() => navigate('/billing/choose-plan')}
                            className="w-full bg-indigo-600 hover:bg-indigo-700"
                        >
                            Try Again
                        </Button>
                        
                        <Link
                            to="/dashboard"
                            className="block text-sm text-gray-500 hover:text-gray-700"
                        >
                            Return to Dashboard
                        </Link>
                    </div>
                </div>
            </div>
            </div>
        </div>
    );
}
